import { useMemo } from "react";
import { auth } from "./firebase";
import { signOut } from "firebase/auth";
import { useResults } from "./lib/ResultsContext";

export default function Dashboard({ user, onLogout }) {
  var results = useResults();
  var allMatches = results.matches;
  var allRankings = results.rankings;
  var playerName = user.playerName ? results.canon(user.playerName) : user.playerName;

  var stats = useMemo(function() {
    if (!playerName) return null;
    var myMatches = allMatches.filter(function(m) {
      return m.player1 === playerName || m.player2 === playerName;
    });
    var wins = 0, losses = 0, winAvgs = [], lossAvgs = [], opponents = {};
    myMatches.forEach(function(m) {
      var isP1 = m.player1 === playerName;
      var won = (isP1 && m.score1 > m.score2) || (!isP1 && m.score2 > m.score1);
      var opp = isP1 ? m.player2 : m.player1;
      if (!opponents[opp]) opponents[opp] = { w: 0, l: 0 };
      if (won) {
        wins++;
        opponents[opp].w++;
        if (m.winner_avg) winAvgs.push(parseFloat(m.winner_avg));
      } else {
        losses++;
        opponents[opp].l++;
        if (m.loser_avg) lossAvgs.push(parseFloat(m.loser_avg));
      }
    });
    var allAvgs = winAvgs.concat(lossAvgs);
    var avg = allAvgs.length > 0 ? (allAvgs.reduce(function(a,b){return a+b;},0)/allAvgs.length).toFixed(2) : null;
    var bestAvg = allAvgs.length > 0 ? Math.max.apply(null, allAvgs).toFixed(2) : null;
    var winPct = (wins + losses) > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;

    // H2H sorted by most played
    var h2h = Object.keys(opponents).map(function(opp) {
      return { opp: opp, w: opponents[opp].w, l: opponents[opp].l, total: opponents[opp].w + opponents[opp].l };
    }).sort(function(a,b){ return b.total - a.total; });

    // Top 5 highest ranked opponents beaten
    var rankedBeaten = Object.keys(opponents).filter(function(opp) {
      return opponents[opp].w > 0;
    }).map(function(opp) {
      var idx = allRankings.findIndex(function(r){ return r.player === opp; });
      return { opp: opp, rank: idx !== -1 ? idx + 1 : null, w: opponents[opp].w };
    }).filter(function(x){ return x.rank !== null; })
      .sort(function(a,b){ return a.rank - b.rank; })
      .slice(0, 5);

    // Recent 10 matches
    var recent = myMatches.slice().sort(function(a,b){ return b.date.localeCompare(a.date); }).slice(0, 10);

    return { total: wins + losses, wins: wins, losses: losses, winPct: winPct, avg: avg, bestAvg: bestAvg, h2h: h2h, recent: recent, rankedBeaten: rankedBeaten };
  }, [playerName, allMatches, allRankings]);

  var ranking = useMemo(function() {
    if (!playerName) return null;
    var idx = allRankings.findIndex(function(r){ return r.player === playerName; });
    if (idx === -1) return null;
    var r = allRankings[idx];
    return { rank: idx + 1, total_points: r.total_points, prev_rank: r.prev_rank, prev_points: r.prev_points, results: r.results };
  }, [playerName, allRankings]);

  function handleLogout() {
    signOut(auth).then(onLogout);
  }

  var sectionTitle = function(label) {
    return <div style={{ fontSize: 12, color: "#FFB020", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 12, paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{label}</div>;
  };

  var statBox = function(label, value, colour) {
    return (
      <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: colour || "#fff" }}>{value}</div>
        <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 3 }}>{label}</div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "24px 20px", color: "#fff", fontFamily: "sans-serif" }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Logged in as</div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#FFB020" }}>{playerName || user.email}</h2>
          {playerName && <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>{user.email}</div>}
        </div>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#888", padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>Log Out</button>
      </div>

      {!playerName && (
        <div style={{ background: "rgba(255,165,0,0.08)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 24, fontSize: 14, color: "#FFB020" }}>
          Your account isn't linked to a player name yet. Please contact EDC to link your account.
        </div>
      )}

      {stats && (
        <>
          {/* Ranking */}
          {ranking && (
            <div style={{ marginBottom: 28 }}>
              {sectionTitle("🏆 Current Ranking")}
              <div style={{ background: "rgba(255,176,32,0.06)", border: "1px solid rgba(255,176,32,0.2)", borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                <div style={{ textAlign: "center", minWidth: 80 }}>
                  <div style={{ fontSize: 48, fontWeight: 800, color: "#FFB020", lineHeight: 1 }}>{"#" + ranking.rank}</div>
                  <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>Ranking</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{ranking.total_points.toLocaleString() + " pts"}</div>
                  {ranking.prev_rank && (
                    <div style={{ fontSize: 13, color: ranking.prev_rank > ranking.rank ? "#6DC878" : ranking.prev_rank < ranking.rank ? "#FF6B6B" : "#666", marginTop: 4 }}>
                      {ranking.prev_rank > ranking.rank ? "▲ Up " + (ranking.prev_rank - ranking.rank) : ranking.prev_rank < ranking.rank ? "▼ Down " + (ranking.rank - ranking.prev_rank) : "— No change"}
                      {" from #" + ranking.prev_rank}
                      {ranking.prev_points && <span style={{ color: "#555", marginLeft: 8 }}>{"(was " + ranking.prev_points + " pts)"}</span>}
                    </div>
                  )}
                  <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 10, overflow: "hidden", maxWidth: 300 }}>
                    <div style={{ height: "100%", width: Math.round((ranking.total_points / allRankings[0].total_points) * 100) + "%", background: "linear-gradient(90deg,#FFB020,#FF6B00)", borderRadius: 2 }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          <div style={{ marginBottom: 28 }}>
            {sectionTitle("📊 My Stats")}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
              {statBox("Played", stats.total, "#fff")}
              {statBox("Wins", stats.wins, "#6DC878")}
              {statBox("Losses", stats.losses, "#aaa")}
              {statBox("Win %", stats.winPct + "%", "#FFB020")}
              {statBox("Avg", stats.avg || "-", "#7BA7FF")}
            </div>
            {stats.bestAvg && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#555" }}>
                Best average: <span style={{ color: "#7BA7FF", fontWeight: 600 }}>{stats.bestAvg}</span>
              </div>
            )}
          </div>

          {/* Top ranked players beaten */}
          {stats.rankedBeaten.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              {sectionTitle("⭐ Top Ranked Players Beaten")}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {stats.rankedBeaten.map(function(b, bi) {
                  return (
                    <div key={bi} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#FFB020", minWidth: 48, textAlign: "center" }}>{"#" + b.rank}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#ccc" }}>{b.opp}</div>
                        <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{b.w + " win" + (b.w > 1 ? "s" : "") + " against this player"}</div>
                      </div>
                      <div style={{ fontSize: 18 }}>{"🏆"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ranking points breakdown */}
          {ranking && ranking.results.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              {sectionTitle("🏅 Ranking Points Breakdown")}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {ranking.results.map(function(r, ri) {
                  var gc = { "Grade A": "#FFB020", "Grade B": "#7BA7FF", "Grade C": "#6DC878" }[r.grade] || "#aaa";
                  return (
                    <div key={ri} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, color: "#ccc", fontWeight: 500 }}>{r.tournament}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 10, color: gc, border: "1px solid " + gc + "44", borderRadius: 20, padding: "1px 7px" }}>{r.grade}</span>
                          <span style={{ fontSize: 11, color: "#555" }}>{r.draw === "Consolation" ? "[Cons] " : ""}{r.stage}</span>
                          <span style={{ fontSize: 11, color: r.outcome === "W" ? "#6DC878" : "#888" }}>{r.outcome === "W" ? "Won" : "Lost"}</span>
                          <span style={{ fontSize: 11, color: "#444" }}>{new Date(r.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#FFB020" }}>{"+" + r.points}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent results */}
          <div style={{ marginBottom: 28 }}>
            {sectionTitle("🎯 Recent Results")}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {stats.recent.map(function(m, mi) {
                var isP1 = m.player1 === playerName;
                var won = (isP1 && m.score1 > m.score2) || (!isP1 && m.score2 > m.score1);
                var opp = isP1 ? m.player2 : m.player1;
                var myScore = isP1 ? m.score1 : m.score2;
                var oppScore = isP1 ? m.score2 : m.score1;
                var myAvg = won ? m.winner_avg : m.loser_avg;
                return (
                  <div key={mi} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: won ? "#6DC878" : "#FF6B6B", minWidth: 28 }}>{won ? "W" : "L"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, color: "#ccc" }}>{myScore + " - " + oppScore + " vs " + opp}</div>
                      <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                        {m.tournament + " · " + m.stage}
                        {myAvg && <span style={{ marginLeft: 8, color: "#444" }}>{"avg " + myAvg}</span>}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#555" }}>{new Date(m.date).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* H2H */}
          <div>
            {sectionTitle("⚔️ Head to Head Record")}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
              {stats.h2h.slice(0, 20).map(function(h, hi) {
                var winPct = Math.round((h.w / h.total) * 100);
                return (
                  <div key={hi} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 13, color: "#ccc", marginBottom: 6, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.opp}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: h.w > h.l ? "#6DC878" : h.w < h.l ? "#FF6B6B" : "#aaa" }}>{h.w + " - " + h.l}</span>
                      <span style={{ fontSize: 11, color: "#555" }}>{winPct + "% (" + h.total + " played)"}</span>
                    </div>
                    <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: winPct + "%", background: h.w > h.l ? "#6DC878" : h.w < h.l ? "#FF6B6B" : "#aaa", borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
